import { useEffect, useState } from 'react';

import { BRAND } from '@fin/shared/brand';

import { Button, Card, PageHeader, inputClass } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import {
  getCurrentPushPermission,
  getCurrentSubscription,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';

interface PushSubRow {
  hash: string;
  userAgent?: string;
  createdAt: string;
}

interface AdminRow {
  email: string;
  groups: ('super_admin' | 'admin' | 'marketing')[];
  loginStatus: 'invited' | 'active' | 'unknown';
  createdAt?: string;
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.groups?.includes('super_admin');
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [subs, setSubs] = useState<PushSubRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Admin user management (super_admin only)
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'super_admin' | 'marketing'>('admin');
  const [inviteBusy, setInviteBusy] = useState(false);

  async function refreshAdmins() {
    if (!isSuperAdmin) return;
    setAdminsLoading(true);
    try {
      const res = await api<{ items: AdminRow[] }>('/admins');
      setAdmins(res.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAdminsLoading(false);
    }
  }

  async function onInviteAdmin(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setInviteBusy(true);
    try {
      const res = await api<{ email: string; role: string; emailed: boolean }>('/admins', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), role: inviteRole }),
      });
      setMsg(
        res.emailed
          ? `Invited ${res.email} as ${res.role}. Cognito emailed them a temporary password.`
          : `${res.email} already had a Cognito account — added to '${res.role}' (no new email sent).`,
      );
      setInviteEmail('');
      setInviteRole('admin');
      await refreshAdmins();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setInviteBusy(false);
    }
  }

  async function onRevokeAdmin(email: string) {
    if (!confirm(`Revoke admin access for ${email}? They will no longer be able to sign in to the admin portal.`)) return;
    setErr(null);
    setMsg(null);
    try {
      await api(`/admins/${encodeURIComponent(email)}`, { method: 'DELETE' });
      setMsg(`Revoked admin access for ${email}.`);
      await refreshAdmins();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function refresh() {
    try {
      setPermission(await getCurrentPushPermission());
      const sub = await getCurrentSubscription();
      setEndpoint(sub?.endpoint ?? null);
      const res = await api<{ items: PushSubRow[] }>('/push/subscriptions');
      setSubs(res.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refresh();
    void refreshAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  async function onSubscribe() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await subscribeToPush();
      setMsg('Subscribed. Test it below.');
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onUnsubscribe() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await unsubscribeFromPush();
      setMsg('Unsubscribed on this device.');
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onTest() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api('/push/test', { method: 'POST', body: '{}' });
      setMsg('Test push dispatched. Check your system tray / lock screen.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Account, workspace & integrations"
        icon={PageIcon.settings}
        accent={PageAccent.settings}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
            Account
          </div>
          <div className="divide-y divide-stone-100">
            <Row label="Email">{user?.email ?? '—'}</Row>
            <Row label="Sub">
              <span className="font-mono text-xs">{user?.userId ?? '—'}</span>
            </Row>
            <Row label="Groups">
              {user?.groups?.length ? user.groups.join(', ') : '—'}
            </Row>
          </div>
        </Card>

        <Card>
          <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
            Push notifications (this device)
          </div>
          <div className="divide-y divide-stone-100">
            <Row label="Browser support">{isPushSupported() ? 'yes' : 'no'}</Row>
            <Row label="Permission">{permission}</Row>
            <Row label="Endpoint">
              <span className="font-mono text-xs break-all">
                {endpoint ?? 'not subscribed on this device'}
              </span>
            </Row>
          </div>
          <div className="flex flex-wrap gap-2 px-4 py-3">
            {!endpoint && (
              <Button onClick={onSubscribe} disabled={busy || !isPushSupported()}>
                Enable push
              </Button>
            )}
            {endpoint && (
              <>
                <Button onClick={onTest} disabled={busy}>
                  Send test push
                </Button>
                <Button variant="secondary" onClick={onUnsubscribe} disabled={busy}>
                  Unsubscribe
                </Button>
              </>
            )}
          </div>
        </Card>
        {isSuperAdmin && (
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <div className="text-xs uppercase tracking-wide text-stone-500">
                Admin users ({admins.length})
              </div>
              <span className="text-[11px] text-stone-400">super_admin only</span>
            </div>
            <form onSubmit={onInviteAdmin} className="flex flex-col gap-2 border-b border-stone-100 px-4 py-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-stone-700">Email</label>
                <input
                  type="email"
                  required
                  placeholder="new.admin@fixitninjaz.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className={inputClass}
                  style={{ outlineColor: BRAND.primary }}
                />
              </div>
              <div className="sm:w-48">
                <label className="mb-1 block text-xs font-medium text-stone-700">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'super_admin' | 'marketing')}
                  className={inputClass}
                >
                  <option value="admin">admin</option>
                  <option value="super_admin">super_admin</option>
                  <option value="marketing">marketing (blog only)</option>
                </select>
              </div>
              <Button
                type="submit"
                disabled={inviteBusy || !inviteEmail.trim()}
                title="Send a Cognito invite email with a temporary password. The user will set a new password and enrol TOTP on first sign-in."
              >
                {inviteBusy ? 'Inviting\u2026' : 'Invite admin'}
              </Button>
            </form>
            <p className="px-4 py-2 text-xs text-stone-500">
              Cognito will email a temporary password. The new admin will be forced to set their own password and enrol TOTP on first sign-in.
            </p>
            {adminsLoading ? (
              <div className="px-4 py-6 text-sm text-stone-500">Loading\u2026</div>
            ) : admins.length === 0 ? (
              <div className="px-4 py-6 text-sm text-stone-500">No admins yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Roles</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => {
                    const isSelf = a.email.toLowerCase() === (user?.email ?? '').toLowerCase();
                    return (
                      <tr key={a.email} className="border-b border-stone-100 last:border-0">
                        <td className="px-4 py-3">{a.email}{isSelf ? ' (you)' : ''}</td>
                        <td className="px-4 py-3">{a.groups.join(', ')}</td>
                        <td className="px-4 py-3">
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{
                              backgroundColor:
                                a.loginStatus === 'active' ? '#DCFCE7' : a.loginStatus === 'invited' ? '#FEF3C7' : '#F1F5F9',
                              color:
                                a.loginStatus === 'active' ? '#166534' : a.loginStatus === 'invited' ? '#92400E' : '#475569',
                            }}
                          >
                            {a.loginStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-stone-600">
                          {a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai' }) : '\u2014'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!isSelf && (
                            <Button
                              variant="danger"
                              title={`Remove admin access for ${a.email}. Their Cognito account is kept; you can re-invite later.`}
                              onClick={() => onRevokeAdmin(a.email)}
                            >
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        )}
        <Card className="lg:col-span-2">
          <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
            All subscriptions for this account ({subs.length})
          </div>
          {subs.length === 0 ? (
            <div className="px-4 py-6 text-sm text-stone-500">No subscriptions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-4 py-3">Hash</th>
                  <th className="px-4 py-3">User agent</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.hash} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-3 font-mono text-xs">{s.hash}</td>
                    <td className="px-4 py-3 text-stone-600">{s.userAgent ?? '—'}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {new Date(s.createdAt).toLocaleString('en-AE', { timeZone: 'Asia/Dubai' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {(msg || err) && (
        <div className="mt-4">
          {msg && <p className="text-sm" style={{ color: BRAND.success }}>{msg}</p>}
          {err && <p className="text-sm" style={{ color: BRAND.danger }}>{err}</p>}
        </div>
      )}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3 text-sm">
      <div className="text-stone-500">{label}</div>
      <div className="col-span-2 text-stone-800">{children}</div>
    </div>
  );
}
